import { lazy, Suspense, useLayoutEffect, useState, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { RequireAuth } from "./components/RequireAuth";
import { ForceLoginModal } from "./components/ForceLoginModal";
import { APP_CONFIG } from "./config/appConfig";
import { initGA, setAnalyticsUser } from "./lib/analytics";
import { auth } from "./lib/auth";
import { ROUTES } from "./routes/paths";
import { PreservingRedirect } from "./routes/PreservingRedirect";

const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const MtvPage = lazy(() => import("./pages/MtvPage"));
const MtvDetailPage = lazy(() => import("./pages/MtvDetailPage"));
const VideoCommentsPage = lazy(() => import("./pages/VideoCommentsPage"));
const PlaylistPage = lazy(() => import("./pages/PlaylistPage"));
const CrbtLibraryPage = lazy(() => import("./pages/CrbtLibraryPage"));
const CrbtPreviewPage = lazy(() => import("./pages/CrbtPreviewPage"));
const CrbtPurchasePage = lazy(() => import("./pages/CrbtPurchasePage"));
const PlaylistDetailPage = lazy(() => import("./pages/PlaylistDetailPage"));
const AudioLibraryPage = lazy(() => import("./pages/AudioLibraryPage"));
const DownloadAudioLibraryPage = lazy(() => import("./pages/DownloadAudioLibraryPage"));
const DownloadAppPage = lazy(() => import("./pages/DownloadAppPage"));
const LanguagePage = lazy(() => import("./pages/LanguagePage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const ArtistDetailPage = lazy(() => import("./pages/ArtistDetailPage"));
const ArtistsPage = lazy(() => import("./pages/ArtistsPage"));
const CatalogPage = lazy(() => import("./pages/CatalogPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const AtomSubscriptionPage = lazy(() => import("./pages/AtomSubscriptionPage"));
const AyaPayPage = lazy(() => import("./pages/AyaPayPage"));
const CRBTUnsubscribeOTPPage = lazy(() => import("./pages/CRBTUnsubscribeOTPPage"));
const AtomAuthPage = lazy(() => import("./pages/AtomAuthPage"));
const MenuPage = lazy(() => import("./pages/MenuPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const EditProfilePage = lazy(() => import("./pages/EditProfilePage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const RadioPage = lazy(() => import("./pages/RadioPage"));
const UploadedSongsPage = lazy(() => import("./pages/UploadedSongsPage"));
const UploadLibraryPage = lazy(() => import("./pages/UploadLibraryPage"));
const UploadProgressPage = lazy(() => import("./pages/UploadProgressPage"));
const CreatePlaylistPage = lazy(() => import("./pages/CreatePlaylistPage"));
const BrowsePlaylistPage = lazy(() => import("./pages/BrowsePlaylistPage"));
const AddToPlaylistPage = lazy(() => import("./pages/AddToPlaylistPage"));
const ConfirmPlaylistPage = lazy(() => import("./pages/ConfirmPlaylistPage"));
const PlayScreenPage = lazy(() => import("./pages/PlayScreenPage"));
const QueuePage = lazy(() => import("./pages/QueuePage"));
const VoiceTestPage = lazy(() => import("./pages/VoiceTestPage"));
import { startAutoFlush, stopAutoFlush, registerUnloadHandler } from "./lib/artistUsage";

const NotificationSettingPage = lazy(() => import("./pages/NotificationSettingPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const ChangeEmailPage = lazy(() => import("./pages/ChangeEmailPage"));
const ChangePhonePage = lazy(() => import("./pages/ChangePhonePage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const LoginHistoryPage = lazy(() => import("./pages/LoginHistoryPage"));
const ArtistUsagePage = lazy(() => import("./pages/ArtistUsagePage"));
const AddPhoneNumberPage = lazy(() => import("./pages/AddPhoneNumberPage"));
const AddPasswordPage = lazy(() => import("./pages/AddPasswordPage"));

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search]);

  return null;
}

function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

export default function App() {
  const [useSimulator, setUseSimulator] = useState(() => {
    try {
      const stored = localStorage.getItem("ym_use_simulator");
      return stored !== "false";
    } catch {
      return true;
    }
  });

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return false;
    const isDev = import.meta.env.DEV;
    return isDev && window.self === window.top && window.innerWidth > 500 && useSimulator;
  });

  const [simulatorWidth, setSimulatorWidth] = useState(() => {
    try {
      const stored = localStorage.getItem("ym_simulator_width");
      return stored || "390px";
    } catch {
      return "390px";
    }
  });

  const [showDropdown, setShowDropdown] = useState(false);

  const changeSimulatorWidth = (width: string) => {
    try {
      localStorage.setItem("ym_simulator_width", width);
    } catch (e) {}
    setSimulatorWidth(width);
  };

  useEffect(() => {
    const handleResize = () => {
      const isDev = import.meta.env.DEV;
      setIsDesktop(isDev && window.self === window.top && window.innerWidth > 500 && useSimulator);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [useSimulator]);

  const toggleSimulator = (val: boolean) => {
    try {
      localStorage.setItem("ym_use_simulator", val ? "true" : "false");
    } catch (e) {}
    setUseSimulator(val);
    const isDev = import.meta.env.DEV;
    setIsDesktop(isDev && window.self === window.top && window.innerWidth > 500 && val);
  };

  const location = useLocation();
  useEffect(() => {
    if (window.self !== window.top && window.top) {
      window.top.history.replaceState(null, "", window.location.pathname + window.location.search + window.location.hash);
    }
  }, [location]);

  useEffect(() => {
    initGA();
    const currentUser = auth.user();
    if (currentUser) {
      setAnalyticsUser(currentUser);
    }
  }, []);

  useEffect(() => {
    startAutoFlush(30_000);
    const unregisterUnload = registerUnloadHandler();
    return () => {
      stopAutoFlush();
      if (unregisterUnload) unregisterUnload();
    };
  }, []);

  if (isDesktop) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100vw",
        height: "100vh",
        background: "#0d0e12",
        overflow: "hidden"
      }}>
        <div style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          zIndex: 10000
        }}>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              title={`Change Device Width (Current: ${simulatorWidth})`}
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "50%",
                border: "1px solid #333",
                background: "rgba(25, 25, 28, 0.95)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backdropFilter: "blur(10px)",
                outline: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                transition: "background-color 0.2s"
              }}
              onMouseOver={(e) => e.currentTarget.style.background = "rgba(45, 45, 48, 0.95)"}
              onMouseOut={(e) => e.currentTarget.style.background = "rgba(25, 25, 28, 0.95)"}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                <line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>
            </button>

            {showDropdown && (
              <div style={{
                position: "absolute",
                top: "0",
                right: "48px",
                background: "rgba(25, 25, 28, 0.98)",
                border: "1px solid #333",
                borderRadius: "12px",
                padding: "6px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                backdropFilter: "blur(15px)",
                minWidth: "160px"
              }}>
                {[
                  { value: "375px", label: "iPhone SE (375px)" },
                  { value: "390px", label: "iPhone 13/14 (390px)" },
                  { value: "412px", label: "Pixel 7/8 (412px)" },
                  { value: "430px", label: "iPhone Pro Max (430px)" }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      changeSimulatorWidth(opt.value);
                      setShowDropdown(false);
                    }}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "6px",
                      background: simulatorWidth === opt.value ? "rgba(255,255,255,0.08)" : "transparent",
                      border: "none",
                      color: simulatorWidth === opt.value ? "#ff6b6b" : "white",
                      fontSize: "12px",
                      fontWeight: "bold",
                      textAlign: "left",
                      cursor: "pointer",
                      transition: "background 0.2s"
                    }}
                    onMouseOver={(e) => {
                      if (simulatorWidth !== opt.value) e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}
                    onMouseOut={(e) => {
                      if (simulatorWidth !== opt.value) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button
            onClick={() => toggleSimulator(false)}
            title="Full Desktop Web"
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "50%",
              border: "1px solid #333",
              background: "rgba(25, 25, 28, 0.95)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backdropFilter: "blur(10px)",
              outline: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
              transition: "background-color 0.2s"
            }}
            onMouseOver={(e) => e.currentTarget.style.background = "rgba(45, 45, 48, 0.95)"}
            onMouseOut={(e) => e.currentTarget.style.background = "rgba(25, 25, 28, 0.95)"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </button>
        </div>
        <div style={{
          width: simulatorWidth,
          height: "100vh",
          background: "#171717",
          position: "relative",
          transition: "width 0.2s ease-in-out"
        }}>
          <iframe
            src={window.location.href}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block"
            }}
            title="Mobile View"
          />
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary>
      <AppShell>
        <ScrollToTop />
        <ForceLoginModal />
        <Suspense fallback={<div className="route-loading">Loading Yours Music…</div>}>
          <Routes>
            {/* ========================================================= */}
            {/* 1. Core & Public Routes                                   */}
            {/* ========================================================= */}
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.ARTISTS} element={<ArtistsPage />} />
            <Route path="/artists/:id" element={<ArtistDetailPage />} />
            <Route path={ROUTES.PLAYLISTS} element={<PlaylistPage />} />
            <Route path="/playlists/:id" element={<PlaylistDetailPage />} />
            <Route path="/albums/:id" element={<PlaylistDetailPage />} />
            <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
            <Route path={ROUTES.LANGUAGE} element={<LanguagePage />} />
            <Route path={ROUTES.DOWNLOAD} element={<ExternalRedirect to={APP_CONFIG.downloadRedirectUrl} />} />

            {/* ========================================================= */}
            {/* 2. Standalone Auth & Onboarding Routes                    */}
            {/* ========================================================= */}
            <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.AUTH.REGISTER} element={<RegisterPage />} />
            <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
            <Route path={ROUTES.AUTH.ADD_PHONE} element={<AddPhoneNumberPage />} />
            <Route path={ROUTES.AUTH.ADD_PASSWORD} element={<AddPasswordPage />} />
            <Route path={ROUTES.AUTH.ATOM} element={<AtomAuthPage />} />

            {/* ========================================================= */}
            {/* 3. Catalog, Media & Player Routes (Authenticated)         */}
            {/* ========================================================= */}
            <Route path={ROUTES.SEARCH} element={<RequireAuth><SearchPage /></RequireAuth>} />
            <Route path={ROUTES.MUSIC} element={<RequireAuth><PlaylistPage /></RequireAuth>} />
            <Route path={ROUTES.TUNES} element={<RequireAuth><CrbtLibraryPage /></RequireAuth>} />
            <Route path="/tunes/:id/preview" element={<RequireAuth><CrbtPreviewPage /></RequireAuth>} />
            <Route path="/tunes/:id/buy" element={<RequireAuth><CrbtPurchasePage /></RequireAuth>} />
            <Route path={ROUTES.AUDIO_LIBRARY} element={<RequireAuth><AudioLibraryPage /></RequireAuth>} />
            <Route path={ROUTES.UPLOADED_SONGS} element={<RequireAuth><UploadedSongsPage /></RequireAuth>} />
            <Route path={ROUTES.UPLOAD_SONGS} element={<RequireAuth><UploadLibraryPage /></RequireAuth>} />
            <Route path={ROUTES.UPLOAD_PROGRESS} element={<RequireAuth><UploadProgressPage /></RequireAuth>} />
            <Route path={ROUTES.VIDEOS} element={<MtvPage />} />
            <Route path="/videos/:id" element={<MtvDetailPage />} />
            <Route path="/videos/:id/comments" element={<VideoCommentsPage />} />
            <Route path={ROUTES.RADIO} element={<RadioPage />} />
            <Route path="/radio/:id" element={<RadioPage />} />
            <Route path={ROUTES.QUEUE} element={<QueuePage />} />
            <Route path={ROUTES.PLAYER} element={<PlayScreenPage />} />
            <Route path={ROUTES.VOICE_TEST} element={<RequireAuth><VoiceTestPage /></RequireAuth>} />

            {/* ========================================================= */}
            {/* 4. Playlist Creation Flow                                 */}
            {/* ========================================================= */}
            <Route path={ROUTES.PLAYLISTS_CREATE} element={<RequireAuth><CreatePlaylistPage /></RequireAuth>} />
            <Route path={ROUTES.PLAYLISTS_CREATE_BROWSE_PATH} element={<RequireAuth><BrowsePlaylistPage /></RequireAuth>} />
            <Route path={ROUTES.PLAYLISTS_CREATE_ADD_PATH} element={<RequireAuth><AddToPlaylistPage /></RequireAuth>} />
            <Route path={ROUTES.PLAYLISTS_CREATE_CONFIRM_PATH} element={<RequireAuth><ConfirmPlaylistPage /></RequireAuth>} />

            {/* ========================================================= */}
            {/* 5. Profile, Settings & Account Management                 */}
            {/* ========================================================= */}
            <Route path={ROUTES.MENU} element={<RequireAuth><MenuPage /></RequireAuth>} />
            <Route path={ROUTES.PROFILE} element={<RequireAuth><ProfilePage /></RequireAuth>} />
            <Route path={ROUTES.PROFILE_EDIT} element={<RequireAuth><EditProfilePage /></RequireAuth>} />
            <Route path={ROUTES.SETTINGS.SECURITY} element={<RequireAuth><SecurityPage /></RequireAuth>} />
            <Route path={ROUTES.SETTINGS.PASSWORD} element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
            <Route path={ROUTES.SETTINGS.EMAIL} element={<RequireAuth><ChangeEmailPage /></RequireAuth>} />
            <Route path={ROUTES.SETTINGS.PHONE} element={<RequireAuth><ChangePhonePage /></RequireAuth>} />
            <Route path={ROUTES.SETTINGS.LOGIN_HISTORY} element={<RequireAuth><LoginHistoryPage /></RequireAuth>} />
            <Route path={ROUTES.SETTINGS.ARTIST_USAGE} element={<RequireAuth><ArtistUsagePage /></RequireAuth>} />
            <Route path={ROUTES.SETTINGS.NOTIFICATIONS} element={<RequireAuth><NotificationSettingPage /></RequireAuth>} />
            <Route path={ROUTES.NOTIFICATIONS} element={<RequireAuth><NotificationsPage /></RequireAuth>} />

            {/* ========================================================= */}
            {/* 6. Subscription & Monetization                            */}
            {/* ========================================================= */}
            <Route path={ROUTES.SUBSCRIPTIONS.ROOT} element={<RequireAuth><SubscriptionPage /></RequireAuth>} />
            <Route path={ROUTES.SUBSCRIPTIONS.ATOM_PATH} element={<AtomSubscriptionPage />} />
            <Route path={ROUTES.SUBSCRIPTIONS.AYA_PAY_PATH} element={<RequireAuth><AyaPayPage /></RequireAuth>} />
            <Route path={ROUTES.SUBSCRIPTIONS.CRBT_UNSUBSCRIBE_PATH} element={<RequireAuth><CRBTUnsubscribeOTPPage /></RequireAuth>} />

            {/* ========================================================= */}
            {/* 7. Backward-Compatibility Redirects (Zero-Breakage Map)   */}
            {/* ========================================================= */}

            {/* Singular resource & media aliases */}
            <Route path="/artist/:id" element={<PreservingRedirect to="/artists/:id" />} />
            <Route path="/playlist/:id" element={<PreservingRedirect to="/playlists/:id" />} />
            <Route path="/album/:id" element={<PreservingRedirect to="/albums/:id" />} />
            <Route path="/mtv" element={<Navigate to="/videos" replace />} />
            <Route path="/mtv/:id" element={<PreservingRedirect to="/videos/:id" />} />
            <Route path="/play" element={<Navigate to="/player" replace />} />

            {/* Legacy flat settings aliases */}
            <Route path="/security" element={<Navigate to="/settings/security" replace />} />
            <Route path="/change-password" element={<Navigate to="/settings/password" replace />} />
            <Route path="/change-email" element={<Navigate to="/settings/email" replace />} />
            <Route path="/change-phone" element={<Navigate to="/settings/phone" replace />} />
            <Route path="/login-history" element={<Navigate to="/settings/login-history" replace />} />
            <Route path="/artist-usage" element={<Navigate to="/settings/artist-usage" replace />} />

            {/* Legacy flat playlist flow aliases */}
            <Route path="/create-playlist" element={<Navigate to="/playlists/create" replace />} />
            <Route path="/create-playlist/browse" element={<Navigate to="/playlists/create/browse" replace />} />
            <Route path="/create-playlist/add" element={<Navigate to="/playlists/create/add" replace />} />
            <Route path="/create-playlist/confirm" element={<Navigate to="/playlists/create/confirm" replace />} />

            {/* Legacy subscription aliases */}
            <Route path="/subscription" element={<Navigate to="/subscriptions" replace />} />
            <Route path="/subscription/atom" element={<Navigate to="/subscriptions/atom" replace />} />
            <Route path="/subscription/AtomSubscription" element={<Navigate to="/subscriptions/atom" replace />} />
            <Route path="/subscription/ayapay" element={<Navigate to="/subscriptions/aya-pay" replace />} />
            <Route path="/subscription/AyaPay" element={<Navigate to="/subscriptions/aya-pay" replace />} />

            {/* Legacy mobile webview & /screen/* aliases */}
            <Route path="/screen/VideoCommentsScreen" element={<Navigate to="/videos" replace />} />
            <Route path="/screen/PlayScreen" element={<Navigate to="/player" replace />} />
            <Route path="/screen/settings/Security" element={<Navigate to="/settings/security" replace />} />
            <Route path="/screen/settings/ChangePassword" element={<Navigate to="/settings/password" replace />} />
            <Route path="/screen/settings/ChangeEmail" element={<Navigate to="/settings/email" replace />} />
            <Route path="/screen/settings/ChangePhone" element={<Navigate to="/settings/phone" replace />} />
            <Route path="/screen/settings/LoginHistory" element={<Navigate to="/settings/login-history" replace />} />
            <Route path="/screen/settings/ArtistUsage" element={<Navigate to="/settings/artist-usage" replace />} />
            <Route path="/screen/settings/Notification.setting" element={<Navigate to="/settings/notifications" replace />} />
            <Route path="/screen/settings/notification" element={<Navigate to="/settings/notifications" replace />} />
            <Route path="/screen/settings/PrivacyPolicy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/screen/CRBTUnsubscribeOTP" element={<Navigate to="/subscriptions/crbt/unsubscribe" replace />} />
            <Route path="/screen/NotificationScreen" element={<Navigate to="/notifications" replace />} />
            <Route path="/screen/Radio/RadioListScreen" element={<Navigate to="/radio" replace />} />
            <Route path="/screen/Radio/FmDetail" element={<Navigate to="/radio" replace />} />
            <Route path="/screen/PackageScreen" element={<Navigate to="/subscriptions" replace />} />
            <Route path="/screen/MyPackageScreen" element={<Navigate to="/subscriptions" replace />} />
            <Route path="/screen/Album" element={<Navigate to="/music" replace />} />
            <Route path="/screen/ArtistDetail" element={<Navigate to="/artists" replace />} />
            <Route path="/screen/createplaylist" element={<Navigate to="/playlists/create" replace />} />
            <Route path="/screen/createplaylist/CreatePlaylistScreen" element={<Navigate to="/playlists/create" replace />} />
            <Route path="/screen/createplaylist/BrowsePlayListScreen" element={<Navigate to="/playlists/create/browse" replace />} />
            <Route path="/screen/createplaylist/browse" element={<Navigate to="/playlists/create/browse" replace />} />
            <Route path="/screen/createplaylist/AddToPlaylistScreen" element={<Navigate to="/playlists/create/add" replace />} />
            <Route path="/screen/createplaylist/add" element={<Navigate to="/playlists/create/add" replace />} />
            <Route path="/screen/createplaylist/ConfirmPlayListScreen" element={<Navigate to="/playlists/create/confirm" replace />} />
            <Route path="/screen/createplaylist/confirm" element={<Navigate to="/playlists/create/confirm" replace />} />
            <Route path="/screen/QueueScreen" element={<Navigate to="/queue" replace />} />
            <Route path="/screen/VoiceTest" element={<Navigate to="/voice-test" replace />} />
            <Route path="/auth/LoginScreen" element={<Navigate to="/login" replace />} />
            <Route path="/auth/RegisterScreen" element={<Navigate to="/register" replace />} />
            <Route path="/auth/ForgotPasswordScreen" element={<Navigate to="/forgot-password" replace />} />
            <Route path="/auth/AddPhoneNumberScreen" element={<Navigate to="/add-phone" replace />} />
            <Route path="/screen/AddPasswordScreen" element={<Navigate to="/add-password" replace />} />
            <Route path="/auth/AuthLoadingScreen" element={<PreservingRedirect to={ROUTES.AUTH.ATOM} />} />
            <Route path="/BrowseTune" element={<Navigate to="/tunes" replace />} />
            <Route path="/screen/CRBTLibrary" element={<Navigate to="/tunes" replace />} />
            <Route path="/MTV" element={<Navigate to="/videos" replace />} />
            <Route path="/screen/MTVMusicScreen" element={<Navigate to="/videos" replace />} />
            <Route path="/screen/VideoPlayScreen" element={<Navigate to="/videos" replace />} />
            <Route path="/downloadapp" element={<Navigate to="/download" replace />} />
            <Route path="/download-app" element={<Navigate to="/download" replace />} />
            <Route path="/screen/DownloadAudioLibraryScreen" element={<Navigate to="/download" replace />} />
            <Route path="/screen/DownloadApp" element={<Navigate to="/download" replace />} />
            <Route path="/library" element={<Navigate to="/menu" replace />} />
            <Route path="/profile/language" element={<Navigate to="/language" replace />} />
            <Route path="/screen/LanguageScreen" element={<Navigate to="/language" replace />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/profile/security" element={<Navigate to="/settings/security" replace />} />

            {/* ========================================================= */}
            {/* 8. Fallback Catch-all                                     */}
            {/* ========================================================= */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppShell>
      {import.meta.env.DEV && window.self === window.top && window.innerWidth > 500 && (
        <button
          onClick={() => toggleSimulator(true)}
          style={{
            position: "fixed",
            bottom: "85px",
            right: "20px",
            padding: "10px 18px",
            borderRadius: "20px",
            border: "1px solid var(--accent)",
            background: "rgba(255, 107, 107, 0.1)",
            color: "var(--accent)",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "bold",
            backdropFilter: "blur(10px)",
            zIndex: 9999
          }}
        >
          Mobile Simulator View
        </button>
      )}
    </AppErrorBoundary>
  );
}
